import React from 'react'
import { Icon } from './icons'

// Inject focus + checkbox/radio/switch styles once — :focus-visible can't be inlined.
if (typeof document !== 'undefined' && !document.getElementById('tn-form-styles')) {
  const s = document.createElement('style')
  s.id = 'tn-form-styles'
  s.textContent = `
    .tn-input, .tn-select, .tn-textarea {
      transition: border-color var(--dur-fast) var(--ease-out),
                  box-shadow   var(--dur-fast) var(--ease-out);
    }
    .tn-input:focus, .tn-input:focus-visible,
    .tn-select:focus, .tn-select:focus-visible,
    .tn-textarea:focus, .tn-textarea:focus-visible {
      border-color: var(--focus-ring-border) !important;
      box-shadow: 0 0 0 3px var(--focus-ring-glow);
      outline: none;
    }
    .tn-input.tn-err:focus, .tn-select.tn-err:focus, .tn-textarea.tn-err:focus {
      border-color: var(--st-error) !important;
      box-shadow: 0 0 0 3px var(--st-error-bg);
    }
    .tn-input::placeholder, .tn-textarea::placeholder { color: var(--text-dim); }
    .tn-check, .tn-radio {
      appearance: none; -webkit-appearance: none;
      width: 18px; height: 18px; flex-shrink: 0;
      background: var(--bg-elevated); border: 1.5px solid var(--border-strong);
      cursor: pointer; display: inline-grid; place-items: center;
      transition: all var(--dur-fast) var(--ease-out); margin: 0; padding: 0;
    }
    .tn-check { border-radius: 4px; }
    .tn-radio { border-radius: 50%; }
    .tn-check:hover, .tn-radio:hover { border-color: var(--brand-gold); }
    .tn-check:checked, .tn-radio:checked { background: var(--brand-gold); border-color: var(--brand-gold); }
    .tn-check:checked::after {
      content: ''; width: 10px; height: 10px; background: var(--brand-gold-ink);
      clip-path: polygon(14% 50%, 0 65%, 40% 100%, 100% 16%, 86% 4%, 38% 71%);
    }
    .tn-radio:checked::after { content: ''; width: 8px; height: 8px; border-radius: 50%; background: var(--brand-gold-ink); }
    .tn-check:focus-visible, .tn-radio:focus-visible { box-shadow: 0 0 0 3px var(--focus-ring-glow); outline: none; }
    .tn-check:disabled, .tn-radio:disabled { opacity: 0.4; cursor: not-allowed; }
  `
  document.head.appendChild(s)
}

// ─── Field ──────────────────────────────────────────────────────────
export interface FieldProps {
  label?: string
  hint?: string
  error?: string
  required?: boolean
  children?: React.ReactNode
  style?: React.CSSProperties
}

export function Field({ label, hint, error, required, children, style = {} }: FieldProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, ...style }}>
      {label && (
        <label style={{
          fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600,
          letterSpacing: 0.1, color: 'var(--text-secondary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          {label}
          {required && <span aria-hidden="true" style={{ color: 'var(--st-error)' }}>*</span>}
        </label>
      )}
      {children}
      {(hint || error) && (
        <span style={{
          fontFamily: 'var(--font-sans)', fontSize: 11.5,
          color: error ? 'var(--st-error)' : 'var(--text-dim)', lineHeight: 1.4,
        }}>{error || hint}</span>
      )}
    </div>
  )
}

// ─── Input ──────────────────────────────────────────────────────────
export interface InputProps {
  value?: string
  onChange?: (value: string, e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  type?: string
  disabled?: boolean
  readOnly?: boolean
  error?: boolean
  icon?: string | null
  iconRight?: string | null
  fullWidth?: boolean
  size?: 'sm' | 'md'
  style?: React.CSSProperties
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  onClick?: React.MouseEventHandler<HTMLInputElement>
  id?: string
  name?: string
  autoComplete?: string
  autoFocus?: boolean
  min?: string | number
  max?: string | number
  step?: string | number
  pattern?: string
  'aria-label'?: string
  'aria-describedby'?: string
}

export function Input({
  value, onChange, placeholder, type = 'text',
  disabled = false, readOnly = false, error = false,
  icon = null, iconRight = null, fullWidth = true,
  size: sizeProp, style = {},
  onFocus, onBlur, onKeyDown, onClick,
  id, name, autoComplete, autoFocus, min, max, step, pattern,
  'aria-label': ariaLabel, 'aria-describedby': ariaDescribedBy,
}: InputProps) {
  const explicitH = sizeProp === 'sm' ? 30 : sizeProp === 'md' ? 36 : null
  const h = explicitH || 'var(--field-h, 36px)'
  const padL = icon ? 36 : 12
  const padR = iconRight ? 36 : 12
  return (
    <div style={{
      position: 'relative', display: 'flex', alignItems: 'center',
      width: fullWidth ? '100%' : 'auto', minWidth: 0, ...style,
    }}>
      {icon && (
        <span style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
          color: 'var(--text-muted)', pointerEvents: 'none', display: 'inline-flex',
        }}>
          <Icon name={icon} size={15} sw={1.7}/>
        </span>
      )}
      <input
        className={`tn-input${error ? ' tn-err' : ''}`}
        type={type} value={value ?? ''}
        onChange={onChange ? (e) => onChange(e.target.value, e) : () => {}}
        readOnly={readOnly || !onChange}
        placeholder={placeholder} disabled={disabled}
        onFocus={onFocus} onBlur={onBlur} onKeyDown={onKeyDown} onClick={onClick}
        id={id} name={name} autoComplete={autoComplete} autoFocus={autoFocus}
        min={min} max={max} step={step} pattern={pattern}
        aria-label={ariaLabel} aria-describedby={ariaDescribedBy}
        style={{
          width: '100%', height: h, padding: `0 ${padR}px 0 ${padL}px`,
          background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
          borderRadius: 'var(--r-md)', color: 'var(--text-primary)',
          fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 500,
          letterSpacing: 0.05, lineHeight: 1, minWidth: 0,
        }}
      />
      {iconRight && (
        <span style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          color: 'var(--text-muted)', pointerEvents: 'none', display: 'inline-flex',
        }}>
          <Icon name={iconRight} size={15} sw={1.7}/>
        </span>
      )}
    </div>
  )
}

// ─── Textarea ───────────────────────────────────────────────────────
export interface TextareaProps {
  value?: string
  onChange?: (value: string, e: React.ChangeEvent<HTMLTextAreaElement>) => void
  placeholder?: string
  rows?: number
  disabled?: boolean
  error?: boolean
  fullWidth?: boolean
  style?: React.CSSProperties
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement>
  id?: string
  name?: string
  autoFocus?: boolean
  'aria-label'?: string
}

export function Textarea({
  value, onChange, placeholder, rows = 4, disabled = false, error = false, fullWidth = true, style = {},
  onFocus, onBlur, onKeyDown, id, name, autoFocus, 'aria-label': ariaLabel,
}: TextareaProps) {
  return (
    <textarea
      className={`tn-textarea${error ? ' tn-err' : ''}`}
      value={value ?? ''}
      onChange={onChange ? (e) => onChange(e.target.value, e) : () => {}}
      readOnly={!onChange}
      placeholder={placeholder} disabled={disabled} rows={rows}
      onFocus={onFocus} onBlur={onBlur} onKeyDown={onKeyDown}
      id={id} name={name} autoFocus={autoFocus} aria-label={ariaLabel}
      style={{
        width: fullWidth ? '100%' : 'auto', padding: '10px 12px',
        background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
        borderRadius: 'var(--r-md)', color: 'var(--text-primary)',
        fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 500,
        lineHeight: 1.5, resize: 'vertical', minHeight: 60, ...style,
      }}
    />
  )
}

// ─── Select ─────────────────────────────────────────────────────────
export interface SelectOption { value: string; label: string }

export interface SelectProps {
  value?: string
  onChange?: (value: string, e: React.ChangeEvent<HTMLSelectElement>) => void
  options?: Array<string | SelectOption>
  placeholder?: string | null
  disabled?: boolean
  error?: boolean
  fullWidth?: boolean
  size?: 'sm' | 'md'
  style?: React.CSSProperties
  onFocus?: React.FocusEventHandler<HTMLSelectElement>
  onBlur?: React.FocusEventHandler<HTMLSelectElement>
  id?: string
  name?: string
  'aria-label'?: string
}

export function Select({
  value, onChange, options = [], placeholder = null,
  disabled = false, error = false, fullWidth = true, size: sizeProp, style = {},
  onFocus, onBlur, id, name, 'aria-label': ariaLabel,
}: SelectProps) {
  const explicitH = sizeProp === 'sm' ? 30 : sizeProp === 'md' ? 36 : null
  const h = explicitH || 'var(--field-h, 36px)'
  return (
    <div style={{
      position: 'relative', display: 'inline-flex',
      width: fullWidth ? '100%' : 'auto', minWidth: 0, ...style,
    }}>
      <select
        className={`tn-select${error ? ' tn-err' : ''}`}
        value={value ?? ''}
        onChange={onChange ? (e) => onChange(e.target.value, e) : () => {}}
        disabled={disabled} onFocus={onFocus} onBlur={onBlur}
        id={id} name={name} aria-label={ariaLabel}
        style={{
          appearance: 'none', WebkitAppearance: 'none',
          width: '100%', height: h, padding: '0 34px 0 12px',
          background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
          borderRadius: 'var(--r-md)', color: 'var(--text-primary)',
          fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 500,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}>
        {placeholder && <option value="" disabled>{placeholder}</option>}
        {options.map((o) => {
          const v = typeof o === 'string' ? o : o.value
          const l = typeof o === 'string' ? o : o.label
          return <option key={v} value={v}>{l}</option>
        })}
      </select>
      <span style={{
        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
        color: 'var(--text-muted)', pointerEvents: 'none', display: 'inline-flex',
      }}>
        <Icon name="chevD" size={14} sw={2}/>
      </span>
    </div>
  )
}

// ─── Checkbox ───────────────────────────────────────────────────────
export interface CheckboxProps {
  checked?: boolean
  onChange?: (checked: boolean, e: React.ChangeEvent<HTMLInputElement>) => void
  label?: string
  disabled?: boolean
  style?: React.CSSProperties
}

export function Checkbox({ checked, onChange, label, disabled = false, style = {} }: CheckboxProps) {
  return (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 10,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      fontFamily: 'var(--font-sans)', fontSize: 13.5, color: 'var(--text-primary)',
      ...style,
    }}>
      <input
        type="checkbox" className="tn-check"
        checked={!!checked} onChange={(e) => onChange?.(e.target.checked, e)}
        disabled={disabled}
      />
      {label && <span>{label}</span>}
    </label>
  )
}

// ─── Radio ──────────────────────────────────────────────────────────
export interface RadioProps {
  checked?: boolean
  onChange?: (value: string, e: React.ChangeEvent<HTMLInputElement>) => void
  label?: string
  value?: string
  name?: string
  disabled?: boolean
  style?: React.CSSProperties
}

export function Radio({ checked, onChange, label, value, name, disabled = false, style = {} }: RadioProps) {
  return (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 10,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      fontFamily: 'var(--font-sans)', fontSize: 13.5, color: 'var(--text-primary)',
      ...style,
    }}>
      <input
        type="radio" className="tn-radio"
        checked={!!checked} onChange={(e) => onChange?.(value || '', e)}
        value={value} name={name} disabled={disabled}
      />
      {label && <span>{label}</span>}
    </label>
  )
}

// ─── Switch ─────────────────────────────────────────────────────────
export interface SwitchProps {
  checked?: boolean
  onChange?: (checked: boolean, e: React.ChangeEvent<HTMLInputElement>) => void
  label?: string
  disabled?: boolean
  style?: React.CSSProperties
}

export function Switch({ checked, onChange, label, disabled = false, style = {} }: SwitchProps) {
  return (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 10,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      fontFamily: 'var(--font-sans)', fontSize: 13.5, color: 'var(--text-primary)',
      ...style,
    }}>
      <span style={{
        position: 'relative', width: 32, height: 18, borderRadius: 9,
        background: checked ? 'var(--brand-gold)' : 'var(--bg-elevated)',
        border: `1px solid ${checked ? 'var(--brand-gold)' : 'var(--border-strong)'}`,
        transition: 'background var(--dur-fast) var(--ease-out)',
      }}>
        <span style={{
          position: 'absolute', top: 1, left: checked ? 15 : 1,
          width: 14, height: 14, borderRadius: '50%',
          background: checked ? 'var(--brand-gold-ink)' : 'var(--text-muted)',
          transition: 'left var(--dur-fast) var(--ease-out)',
        }}/>
      </span>
      <input
        type="checkbox" checked={!!checked} disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked, e)}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
      />
      {label && <span>{label}</span>}
    </label>
  )
}
